import type { Site } from '../types'
import { spokenLabel } from '../constants/mapConfig'

// Names (substring match, case-insensitive) of built-in browser voices that sound noticeably
// less robotic than the platform default, roughly in preference order within whichever
// local-vs-network tier pickVoiceCandidates() is currently ranking (see there for why local
// voices are tried before network ones at all):
//  - macOS's newer Siri-derived conversational local voices (Reed, Sandy, Shelley — sound
//    markedly more natural than the legacy Samantha default most browsers fall back to)
//  - Windows Edge's network "Online (Natural)"/neural voices (e.g. "Microsoft Aria Online
//    (Natural) - English (United States)") and Chrome's cross-platform "Google US/UK English"
//    remote-synthesis voices — 'natural'/'neural'/'online' only ever match voices in the
//    network tier, since Edge doesn't ship a local voice with those names
const PREFERRED_VOICE_HINTS = [
  'natural', 'neural', 'online', 'premium', 'enhanced',
  'google us english', 'google uk english', 'google',
  'reed', 'sandy', 'shelley',
  'samantha', 'ava', 'nicky', 'aaron', 'zoe',
]

// macOS ships a handful of "novelty" system voices (cartoonish, sung, or whispered) alongside
// the normal ones. They match no positive hint above, but a raw "first English voice" fallback
// could still land on one of these — explicitly exclude them so the fallback stays legible.
const NOVELTY_VOICE_NAMES = [
  'albert', 'bad news', 'bahh', 'bells', 'boing', 'bubbles', 'cellos', 'fred',
  'good news', 'jester', 'organ', 'superstar', 'trinoids', 'whisper', 'wobble', 'zarvox',
]

let cachedVoices: SpeechSynthesisVoice[] = []

function loadVoices() {
  cachedVoices = window.speechSynthesis?.getVoices() ?? []
}

if (typeof window !== 'undefined' && window.speechSynthesis) {
  loadVoices()
  window.speechSynthesis.addEventListener('voiceschanged', loadVoices)
}

// Ranks every usable English voice on the machine, best first, instead of picking just one —
// speakSiteInfo() walks this list and only moves to the next candidate if the current one
// actually fails at runtime, rather than a name/browser guess ruling one out for everyone.
//
// Local (localService === true) voices are ranked ahead of network ones regardless of hint tier:
// this is triggered on hover, and network voices (Edge's "Online (Natural)", Chrome's "Google US
// English") have real, unavoidable network latency — often 1-3s before they even start — since
// they stream from a cloud TTS service. That's not a bug to retry around, it's inherent to how
// they work, and a multi-second hang before a hover-triggered guide starts is worse than a
// slightly-more-robotic voice that speaks instantly. Network voices stay in the list as a
// fallback (still better than dead silence) if no local voice is usable.
function pickVoiceCandidates(): SpeechSynthesisVoice[] {
  const englishVoices = cachedVoices
    .filter(v => v.lang.toLowerCase().startsWith('en'))
    .filter(v => !NOVELTY_VOICE_NAMES.some(n => v.name.toLowerCase().includes(n)))
  if (englishVoices.length === 0) return []

  const ranked: SpeechSynthesisVoice[] = []
  const seen = new Set<string>()
  const add = (v: SpeechSynthesisVoice) => {
    if (!seen.has(v.name)) {
      seen.add(v.name)
      ranked.push(v)
    }
  }

  const byHintTier = (pool: SpeechSynthesisVoice[]) => {
    for (const hint of PREFERRED_VOICE_HINTS) {
      pool.filter(v => v.name.toLowerCase().includes(hint)).forEach(add)
    }
    pool.forEach(add) // anything left in this pool that matched no hint
  }
  byHintTier(englishVoices.filter(v => v.localService))
  byHintTier(englishVoices.filter(v => !v.localService))

  return ranked
}

let pendingSpeak: number | null = null

// Which site's audio is currently scheduled or playing. Speech is a single global resource that
// SiteMarker, SiteListCard and SitePanel all drive, and a component that has already been
// superseded must not be able to silence whoever took over — see cancelSiteAudio().
let owningSiteId: string | null = null

const RATE_STORAGE_KEY = 'a11ySpeechRate'

/** The rate options offered in the UI, as multipliers of normal speaking speed. */
export const SPEECH_RATES = [
  { label: 'Slow', value: 0.7 },
  { label: 'Normal', value: 0.92 },
  { label: 'Fast', value: 1.25 },
] as const

const DEFAULT_SPEECH_RATE = 0.92

/**
 * Playback rate for the audio guide, persisted across visits.
 *
 * A single fixed rate can't serve this audience: the same 0.92 that reads comfortably for one user
 * is unusably slow for a habitual screen-reader user and too fast for someone with a cognitive or
 * processing disability. Read at speak() time rather than captured once, so a change applies to the
 * next utterance without needing to re-plumb anything.
 */
export function getSpeechRate(): number {
  try {
    const raw = window.localStorage.getItem(RATE_STORAGE_KEY)
    if (!raw) return DEFAULT_SPEECH_RATE
    const parsed = Number(raw)
    // Guard the range as well as the parse: the Web Speech API clamps silently, and a corrupt or
    // hand-edited value shouldn't be able to leave the guide inaudibly slow or unintelligibly fast.
    if (!Number.isFinite(parsed) || parsed < 0.5 || parsed > 2) return DEFAULT_SPEECH_RATE
    return parsed
  } catch {
    return DEFAULT_SPEECH_RATE
  }
}

export function setSpeechRate(rate: number) {
  try {
    window.localStorage.setItem(RATE_STORAGE_KEY, String(rate))
  } catch {
    // Private-mode/quota failures shouldn't break speech — the rate just won't persist.
  }
}

export function speakSiteInfo(site: Site, description: string, onEnd: () => void) {
  if (!window.speechSynthesis) return
  owningSiteId = site.id
  if (pendingSpeak !== null) {
    window.clearTimeout(pendingSpeak)
    pendingSpeak = null
  }
  // Only cancel when the engine actually has something to cancel. Moving from one marker straight
  // to another produced two cancel() calls in quick succession — SiteMarker's mouseleave on the
  // first marker fires cancelSiteAudio(), then pointerenter on the second lands here — with the
  // real speak() following only 150ms after the second one. Chromium drops utterances queued too
  // soon after cancel() (the reason for that 150ms delay in the first place), and a redundant
  // cancel() on an already-empty queue restarts that window for nothing, which is what made a
  // quick marker-to-marker move silent. Guarding on the engine's own reported state keeps the
  // cancel where it's needed (an utterance genuinely still speaking or queued) and skips it where
  // it only pushes the next utterance further inside Chromium's drop window.
  if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
    window.speechSynthesis.cancel()
  }

  // Short, separately-punctuated sentences read more naturally than one long run-on —
  // each full stop gives the voice a place to take a breath instead of racing through commas.
  const parts = [`${site.name}.`]
  if (site.org) parts.push(`${site.org}.`)
  if (site.address) parts.push(`${site.address}.`)
  if (description) parts.push(description)
  // Speech engines read the bare "AT" unpredictably (often as the word "at"), so the audio
  // guide speaks the expanded form — the same substitution the aria-labels use, applied to the
  // whole utterance rather than per-field.
  const text = spokenLabel(parts.join(' '))

  // Chromium (Edge included) has a long-standing bug where speak() called immediately after
  // cancel() silently drops the new utterance — cancel()'s internal teardown hasn't finished
  // yet, most reproducible on Windows/Edge. A short delay lets that finish before queuing the
  // next utterance. It doubles as a debounce for SiteMarker's hover-triggered calls, so sweeping
  // the mouse across several markers doesn't fire (and drop) a new utterance every few pixels.
  pendingSpeak = window.setTimeout(() => {
    pendingSpeak = null
    // getVoices()/voiceschanged can still be settling on first use — refresh if we're about to
    // speak with an empty cache instead of trusting whatever was cached at module load.
    if (cachedVoices.length === 0) loadVoices()
    attemptSpeak(text, pickVoiceCandidates(), 0, onEnd)
  }, 150)
}

function attemptSpeak(text: string, candidates: SpeechSynthesisVoice[], index: number, onEnd: () => void) {
  const utt = new SpeechSynthesisUtterance(text)
  // Once candidates are exhausted, leave voice unset (null) for one last attempt on whatever
  // the browser's own default is, rather than giving up before even trying that.
  utt.voice = candidates[index] ?? null
  // Rate is user-controllable (see speechRate below); everything else stays fixed regardless of
  // voice/browser. Safari's Ava/Reed at the 0.92 default is the reference the audio guide is tuned
  // against. Browsers still can't be made to use the exact same underlying voice (Apple's
  // Siri-derived voices are macOS-only, invisible to the Web Speech API on Windows/Linux), but
  // pacing shouldn't also drift on top of that.
  utt.rate = getSpeechRate()
  // The Web Speech API has no EQ/treble control — pitch is the only lever it exposes that shifts
  // the voice's frequency content, so it's the closest available fix for a "sounds muffled"
  // report. 1.08 is a small nudge toward brighter/crisper, not enough to sound unnatural.
  utt.pitch = 1.08
  utt.onend = onEnd
  utt.onerror = e => {
    // cancel() (a fresh hover, a new site selected, toggling the guide off) fires 'error' with
    // 'canceled'/'interrupted' on the utterance it just replaced — that's expected, not a voice
    // failure, and retrying here would race a stale attempt against whatever call superseded it.
    if (e.error === 'canceled' || e.error === 'interrupted') return
    console.warn(`[audioGuide] speechSynthesis error with voice "${utt.voice?.name ?? '(default)'}":`, e.error)
    if (index < candidates.length) {
      // Real failure, not a guess — move to the next-best candidate and try again.
      attemptSpeak(text, candidates, index + 1, onEnd)
    } else {
      onEnd()
    }
  }
  window.speechSynthesis.speak(utt)
  // Chromium on Windows (Edge included) has a documented bug where the utterance queue can
  // come up in a paused state right after speak() — nothing audibly happens and no error
  // fires. Forcing a resume() is a no-op everywhere else but unsticks Edge when this hits.
  window.speechSynthesis.resume()
}

// Chrome/Edge (Chromium) have a long-standing speechSynthesis bug: the *first* speak() call after
// page load is silently swallowed — no error, no onend — unless it happens synchronously inside a
// direct user gesture (click). This app's real speech only ever fires later, from a hover on a
// site marker, which Chromium doesn't count as a strong-enough gesture to unstick the engine. The
// user experience was "the Start button needs two clicks": the first hover after turning the guide
// on got eaten, and only the next one (effectively the "second click" of the whole flow) worked.
// Firefox has no such restriction, which is why it only ever reproduced in Chrome/Edge. Speaking a
// near-silent, near-instant utterance directly inside the button's click handler primes the engine
// so the first real hover-triggered utterance goes through.
export function unlockSpeechSynthesis() {
  if (!window.speechSynthesis) return
  const utt = new SpeechSynthesisUtterance(' ')
  utt.volume = 0
  window.speechSynthesis.speak(utt)
}

/**
 * Stops site audio. Pass the `siteId` of the component doing the cancelling to make it a no-op
 * unless that component still owns the current audio; omit it to stop unconditionally (turning the
 * whole audio guide off).
 *
 * The ownership check is what fixes hovering between *adjacent* markers. Decluttered markers
 * overlap, so the pointer enters the second marker's box while still inside the first one's: the
 * DOM fires pointerenter on B before mouseleave on A, and A's leave handler was then cancelling
 * the utterance B had just scheduled 150ms out — so B never spoke. Non-adjacent markers don't
 * overlap, leave-A lands before enter-B, and they always worked, which is why this only ever
 * reproduced on markers sitting close together.
 */
export function cancelSiteAudio(siteId?: string) {
  if (siteId !== undefined && siteId !== owningSiteId) return
  owningSiteId = null
  if (pendingSpeak !== null) {
    window.clearTimeout(pendingSpeak)
    pendingSpeak = null
  }
  window.speechSynthesis?.cancel()
}
